import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown,
  faTimes,
  faCheck,
  faSpinner,
  faStar,
  faRocket,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { subscriptionAPI, paymentAPI } from '../services/api';
import '../assets/css/premium-upgrade.css';

const PremiumUpgradeModal = ({ isOpen, onClose, userType }) => {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPlansAndFeatures();
    }
  }, [isOpen]);

  const fetchPlansAndFeatures = async () => {
    try {
      setLoading(true);
      const [plansResponse, featuresResponse] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getFeatures()
      ]);

      if (plansResponse && plansResponse.data) {
        // Handle different response formats
        let plansList = [];
        if (Array.isArray(plansResponse.data)) {
          plansList = plansResponse.data;
        } else if (plansResponse.data.plans && Array.isArray(plansResponse.data.plans)) {
          plansList = plansResponse.data.plans;
        } else if (plansResponse.data.data && Array.isArray(plansResponse.data.data)) {
          plansList = plansResponse.data.data;
        }

        // Filter plans by user type
        const userPlans = plansList.filter(
          plan => plan.user_type === userType || plan.user_type === 'all'
        );

        // If no plans match user type, show all plans
        setPlans(userPlans.length > 0 ? userPlans : plansList);

        // Auto-select the first plan
        const finalPlans = userPlans.length > 0 ? userPlans : plansList;
        if (finalPlans.length > 0) {
          setSelectedPlan(finalPlans[0]);
        }
      }

      if (featuresResponse && featuresResponse.data) {
        // Handle different response formats for features
        let featuresList = [];
        if (Array.isArray(featuresResponse.data)) {
          featuresList = featuresResponse.data;
        } else if (featuresResponse.data.features && Array.isArray(featuresResponse.data.features)) {
          featuresList = featuresResponse.data.features;
        } else if (featuresResponse.data.data && Array.isArray(featuresResponse.data.data)) {
          featuresList = featuresResponse.data.data;
        }
        setFeatures(featuresList);
      }
    } catch (error) {
      showToast('Failed to load plans. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      showToast('Please select a plan', 'error');
      return;
    }

    // Check authentication
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      showToast('Please log in to upgrade to premium', 'error');
      return;
    }

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userEmail = userData.email || '';

    // Validate required fields
    if (!userEmail) {
      showToast('User email not found. Please log in again.', 'error');
      return;
    }

    if (!selectedPlan.price || selectedPlan.price <= 0) {
      showToast('Invalid plan price. Please contact support.', 'error');
      return;
    }

    try {
      setProcessing(true);

      // Initialize payment - New backend structure
      const paymentData = {
        amount: selectedPlan.price, // Amount in Naira (backend converts to kobo)
        payment_type: 'premium_membership', // Required field
        currency: 'NGN',
        callback_url: `${window.location.origin}/premium/verify`,
        subscription_plan_id: selectedPlan.id, // Direct field, not in metadata
        metadata: {
          plan_name: selectedPlan.name,
          user_type: userType,
          user_email: userEmail
        }
      };

      const paymentResponse = await paymentAPI.initializePayment(paymentData);

      // New backend returns checkout_url and payment_id
      const checkoutUrl = paymentResponse?.data?.checkout_url;
      const reference = paymentResponse?.data?.reference;
      const paymentId = paymentResponse?.data?.payment_id;

      if (checkoutUrl && reference) {
        // Store payment info for verification after payment
        localStorage.setItem('pending_payment_id', paymentId);
        localStorage.setItem('pending_payment_reference', reference);
        localStorage.setItem('pending_subscription_plan', selectedPlan.id);
        localStorage.setItem('pending_user_type', userType);

        // Redirect to Monnify payment page
        window.location.href = checkoutUrl;
      } else {
        showToast('Failed to initialize payment. Please try again.', 'error');
      }
    } catch (error) {

      // Show more specific error messages
      let errorMessage = 'Failed to process payment';
      if (error.status === 401) {
        errorMessage = 'Please log in again to continue';
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getPlanIcon = (planName) => {
    if (planName.toLowerCase().includes('premium')) return faCrown;
    if (planName.toLowerCase().includes('pro')) return faRocket;
    return faStar;
  };

  if (!isOpen) return null;

  return (
    <div className="AutoboyExpress-premium-overlay">
      <div className="AutoboyExpress-premium-modal">
        {/* Header */}
        <div className="AutoboyExpress-premium-header">
          <div className="AutoboyExpress-premium-title">
            <FontAwesomeIcon icon={faCrown} className="AutoboyExpress-premium-icon" />
            <h2>Upgrade to Premium</h2>
          </div>
          <button className="AutoboyExpress-premium-close" onClick={onClose} disabled={processing}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`AutoboyExpress-premium-toast AutoboyExpress-premium-toast-${toast.type}`}>
            {toast.message}
          </div>
        )}

        {loading ? (
          <div className="AutoboyExpress-premium-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Loading plans...</p>
          </div>
        ) : (
          <div className="AutoboyExpress-premium-content">
            {/* Plans Section */}
            <div className="AutoboyExpress-premium-plans">
              <h3>Choose Your Plan</h3>
              <div className="AutoboyExpress-premium-plans-grid">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`AutoboyExpress-premium-plan-card ${
                      selectedPlan?.id === plan.id ? 'selected' : ''
                    } ${plan.is_popular ? 'popular' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    {plan.is_popular && (
                      <div className="AutoboyExpress-premium-popular-badge">
                        Most Popular
                      </div>
                    )}

                    <div className="AutoboyExpress-premium-plan-icon">
                      <FontAwesomeIcon icon={getPlanIcon(plan.name)} />
                    </div>

                    <h4>{plan.name}</h4>
                    {plan.description && <p className="AutoboyExpress-premium-plan-description">{plan.description}</p>}

                    <div className="AutoboyExpress-premium-plan-price">
                      <span className="price">{formatPrice(plan.price)}</span>
                      {/* Backend field is duration_months (an int), not
                          duration — this always rendered "/undefined" on the
                          actual paid-conversion screen. */}
                      <span className="period">/{plan.duration_months === 1 ? 'month' : `${plan.duration_months} months`}</span>
                    </div>

                    {/* Backend sends a pre-formatted "savings" string (e.g.
                        "17% OFF (Save ₦20,000)"), not a discount_percentage
                        number — that field never existed, so the discount
                        badge never rendered on the yearly plan. */}
                    {plan.savings && (
                      <div className="AutoboyExpress-premium-plan-discount">
                        {plan.savings}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Features Section */}
            {selectedPlan && (
              <div className="AutoboyExpress-premium-features">
                <h3>What You'll Get</h3>
                <div className="AutoboyExpress-premium-features-list">
                  {/* GetSubscriptionPlans already returns each plan's own
                      real, plan-specific feature list (a plain string array —
                      yearly plans include extras like "Dedicated Account
                      Manager" monthly doesn't). The separate `features` state
                      (GetPremiumFeatures) is generic per user-type, not
                      per-plan, and its objects have no plan_id/plan_name at
                      all — this filter was therefore always empty and this
                      real, fetched data was never shown to anyone. */}
                  {(selectedPlan.features || []).map((featureName, index) => (
                    <div key={index} className="AutoboyExpress-premium-feature-item">
                      <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      <div>
                        <strong>{featureName}</strong>
                      </div>
                    </div>
                  ))}

                  {/* Fallback only if this plan genuinely has no feature list at all */}
                  {(!selectedPlan.features || selectedPlan.features.length === 0) && (
                    <>
                      <div className="AutoboyExpress-premium-feature-item">
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        <div>
                          <strong>Priority Support</strong>
                          <p>Get faster response times and dedicated support</p>
                        </div>
                      </div>
                      <div className="AutoboyExpress-premium-feature-item">
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        <div>
                          <strong>Advanced Analytics</strong>
                          <p>Deep insights into your {userType === 'seller' ? 'sales' : 'shopping'} patterns</p>
                        </div>
                      </div>
                      <div className="AutoboyExpress-premium-feature-item">
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        <div>
                          <strong>Exclusive Features</strong>
                          <p>Access to premium-only tools and capabilities</p>
                        </div>
                      </div>
                      {userType === 'seller' && (
                        <>
                          <div className="AutoboyExpress-premium-feature-item">
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                            <div>
                              <strong>Boosted Listings</strong>
                              <p>Your products appear higher in search results</p>
                            </div>
                          </div>
                        </>
                      )}
                      {userType === 'buyer' && (
                        <>
                          <div className="AutoboyExpress-premium-feature-item">
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                            <div>
                              <strong>Early Access</strong>
                              <p>See new flash sales before regular users — up to 2 days first</p>
                            </div>
                          </div>
                          <div className="AutoboyExpress-premium-feature-item">
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                            <div>
                              <strong>Exclusive Deals</strong>
                              <p>Access to premium-only discounts and offers</p>
                            </div>
                          </div>
                          <div className="AutoboyExpress-premium-feature-item">
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                            <div>
                              <strong>Premium Packaging</strong>
                              <p>Every order arrives in premium packaging</p>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Security Badge */}
            <div className="AutoboyExpress-premium-security">
              <FontAwesomeIcon icon={faShieldAlt} />
              <span>Secure payment powered by Monnify</span>
            </div>

            {/* Action Buttons */}
            <div className="AutoboyExpress-premium-actions">
              <button
                className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-secondary"
                onClick={onClose}
                disabled={processing}
              >
                Maybe Later
              </button>
              <button
                className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-primary"
                onClick={handleSubscribe}
                disabled={processing || !selectedPlan}
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCrown} />
                    <span>Upgrade Now - {selectedPlan ? formatPrice(selectedPlan.price) : ''}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;
